import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiFileText, FiDownload, FiSend, FiFlag, FiEye, FiTag } from 'react-icons/fi';
import OfferModal from './OfferModal';
import quotationsService from '../../../../services/quotations.service';
import { useAuth } from '../../../../context/AuthContext';
import SectionHelpModal from '../../../../components/common/SectionHelpModal';
import AppTour from '../../../../components/common/AppTour';
import Pagination from '../../../../components/common/Pagination';
import { SiTicktick } from 'react-icons/si';
const QuotationList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showBanner, setShowBanner] = useState(true);
    const [runTour, setRunTour] = useState(false);
    const [selectedQuotationForOffer, setSelectedQuotationForOffer] = useState(null);
    const [showOfferModal, setShowOfferModal] = useState(false);

    // --- Functions ---

    const fetchQuotations = async (page = 1, search = '', estado = '') => {
        try {
            setLoading(true);
            const params = { page, search };
            if (estado) params.estado = estado;
            const data = await quotationsService.getAll(params);

            if (data?.results) {
                setQuotations(data.results);
                setTotalCount(data.count || 0);
            } else if (Array.isArray(data)) {
                setQuotations(data);
                setTotalCount(data.length);
            } else {
                setQuotations([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error('Error fetching quotations:', err);
            setError('Error al cargar cotizaciones');
        } finally {
            setLoading(false);
        }
    };

    // --- Hooks ---

    // Reacciona a cambios en la URL (filtros por parámetro y tour)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('pending') === 'true') {
            setStatusFilter('BORRADOR');
        }
        if (params.get('tour') === 'cotizaciones') {
            setRunTour(true);
        }
    }, [location.search]);

    // Efecto para buscar y paginar
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchQuotations(currentPage, searchTerm, statusFilter);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [currentPage, searchTerm, statusFilter]);


    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta cotización?')) {
            try {
                await quotationsService.delete(id);
                setQuotations(quotations.filter(q => q.id !== id));
            } catch (err) {
                console.error('Error deleting quotation:', err);
                alert('No se pudo eliminar la cotización.');
            }
        }
    };

    const handleResend = async (id, method) => {
        if (!window.confirm('¿Re-enviar la notificación de esta cotización?')) return;
        try {
            const response = await quotationsService.resend(id);
            if (response.whatsapp_link) {
                window.open(response.whatsapp_link, '_blank');
            } else {
                alert('Notificación re-enviada correctamente.');
            }
        } catch (err) {
            console.error('Error resending:', err);
            alert('Error al re-enviar la notificación.');
        }
    };

    const handleDownloadPdf = async (id, numero) => {
        try {
            const blob = await quotationsService.generatePdf(id);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `cotizacion-${numero}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Error downloading PDF:', err);
            alert('Error al generar el PDF');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
    };

    // Ya no necesitamos filteredQuotations local
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const cotizacionesTourSteps = [
        {
            element: '#tour-cotizaciones-header',
            intro: '<strong>Centro de Ventas</strong>Aquí verás todas las cotizaciones que has creado y en qué estado se encuentran ("Borrador", "Enviada", etc.).',
            position: 'bottom',
        },
        {
            element: '#tour-cotizaciones-filtros',
            intro: '<strong>Filtros Rápidos</strong>Usa estas opciones para encontrar cotizaciones de un cliente específico o ver cuáles faltan por enviar.',
            position: 'bottom',
        },
        {
            element: '#tour-cotizaciones-table',
            intro: '<strong>Acciones Rápidas</strong>Desde aquí puedes descargar la cotización en PDF, enviarla por WhatsApp y cambiar el estado cuando te la aprueben.',
            position: 'top',
        },
        {
            element: '#tour-cotizaciones-nueva',
            intro: '<strong>Nueva Cotización</strong>Desde aquí empezarás a armar propuestas con todo lo que ya configuraste. ¡Pero antes, vamos a ver los Impuestos!',
            position: 'left',
        }
    ];

    if (loading && quotations.length === 0) return <div className="loading-overlay"><div className="spinner"></div></div>;

    return (
        <div className="container-custom">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={cotizacionesTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Impuestos' }}
                    onTourComplete={() => {
                        window.location.href = '/app/cotizaplus/impuestos?tour=impuestos';
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-cotizaciones-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Gestión de Cotizaciones</h2>
                        <SectionHelpModal
                            title="¿Cómo funciona tu Bandeja de Ventas?"
                            content={
                                <div>
                                    <p>Este es el corazón de IdevPlus. Aquí se guardan y gestionan todas las propuestas comerciales que creas para tus clientes.</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Diferentes Estados:</strong> Cada cotización tiene un ciclo de vida. Empieza como "Borrador" hasta que le envías el PDF al cliente. Luego cambia a "Enviada" y finalmente tú decides si cerrarla como "Aceptada" o "Rechazada".</li>
                                        <li><strong>Descuentos Especiales:</strong> Recuerda que si un vendedor (Asesor) intenta dar un descuento que supere el límite que fijaste, la cotización no podrá ser enviada hasta que tú la apruebes desde tu Panel de Solicitudes.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Crea y administra tus cotizaciones comerciales</p>
                </div>
                <button id="tour-cotizaciones-nueva" className="btn-custom btn-primary d-none d-lg-flex" onClick={() => navigate('/app/cotizaplus/cotizaciones/nuevo')}>
                    <FiPlus size={20} /> Nueva Cotización
                </button>
            </div>

            {error && <div className="alert-custom alert-danger">{error}</div>}

            <div className="card-custom">
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }} id="tour-cotizaciones-filtros">
                    <div className="header-search" style={{ flex: 1, minWidth: '300px' }}>
                        <FiSearch size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por cliente o número..."
                            className="header-search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <select
                        className="form-control-custom"
                        style={{ width: '200px' }}
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                    >
                        <option value="">Todos los estados</option>
                        <option value="BORRADOR">Borrador</option>
                        <option value="ENVIADA">Enviada</option>
                        <option value="ACEPTADA">Aceptada</option>
                        <option value="RECHAZADA">Rechazada</option>
                    </select>
                </div>

                {/* Banner Ofertas - Oculto hasta futuro módulo CRM
                {showBanner && user?.rol === 'ADMIN' && (
                    <div style={{
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'relative'
                    }}>
                        <button
                            onClick={() => setShowBanner(false)}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'none',
                                border: 'none',
                                color: '#93c5fd',
                                cursor: 'pointer',
                                fontSize: '18px',
                                lineHeight: 1
                            }}
                        >
                            &times;
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px'
                            }}>
                                🚀
                            </div>
                            <div>
                                <h4 style={{ margin: 0, color: '#1e40af', fontSize: '16px' }}>¡Aumenta tus ventas con Mensajería Automática!</h4>
                                <p style={{ margin: '5px 0 0 0', color: '#3b82f6', fontSize: '14px' }}>
                                    Activa el seguimiento automático de ofertas y recupera clientes indecisos.
                                </p>
                            </div>
                        </div>
                        <button
                            className="btn-custom"
                            style={{ backgroundColor: 'white', color: '#3b82f6', border: '1px solid #3b82f6', marginRight: '20px' }}
                            onClick={() => navigate('/app/cotizaplus/ofertas')}
                        >
                            Configurar Ahora
                        </button>
                    </div>
                )}
                */}

                {quotations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }} id="tour-cotizaciones-table">
                        <FiFileText size={48} color="var(--gray-400)" />
                        <p className="text-muted" style={{ marginTop: '10px' }}>No se encontraron cotizaciones</p>
                    </div>
                ) : (
                    <>
                        {/* Vista Desktop: Tabla Clásica */}
                        <div className="table-responsive d-none d-lg-block" id="tour-cotizaciones-table">
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th>Número</th>
                                        <th>Cliente</th>
                                        <th>Fecha</th>
                                        <th>Total</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotations.map((quotation) => {
                                        const statusInfo = quotationsService.getStatusInfo(quotation.estado);
                                        return (
                                            <tr
                                                key={quotation.id}
                                                onClick={() => {
                                                    // Determinar la URL según rol y estado (misma lógica que los botones)
                                                    if (quotation.estado === 'BORRADOR') {
                                                        if (user?.rol === 'ADMIN') {
                                                            navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}`);
                                                        } else { // EMPLEADO
                                                            navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=approval`);
                                                        }
                                                    } else { // ENVIADA, ACEPTADA, RECHAZADA
                                                        navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=view`);
                                                    }
                                                }}
                                                style={{ cursor: 'pointer' }}
                                                className="clickable-row"
                                            >
                                                <td><span style={{ fontWeight: 'bold' }}>{quotation.numero}</span></td>
                                                 <td>
                                                    <div style={{ fontWeight: '500' }}>{quotation.cliente_info?.nombre || 'N/A'}</div>
                                                    {quotation.es_oferta && (
                                                        <div style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            fontSize: '11px',
                                                            color: '#059669',
                                                            background: '#d1fae5',
                                                            padding: '1px 8px',
                                                            borderRadius: '4px',
                                                            fontWeight: '700',
                                                            marginTop: '2px',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            <FiTag size={10} /> Oferta
                                                        </div>
                                                    )}
                                                 </td>
                                                <td>{new Date(quotation.fecha_creacion).toLocaleDateString('es-CL')}</td>
                                                <td style={{ fontWeight: 'bold' }}>{formatCurrency(quotation.total)}</td>
                                                <td>
                                                    <span className={`badge-custom ${statusInfo.class}`}>
                                                        {statusInfo.label}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        {/* Botón Revisar/Aprobar (Visible para Empleados o Admin en Borrador) */}
                                                        {quotation.estado === 'BORRADOR' && (
                                                            <button
                                                                className="btn-icon action-btn-custom"
                                                                onClick={(e) => { e.stopPropagation(); navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=approval`); }}
                                                                title="Revisar / Aprobar Envío"
                                                                style={{ color: 'var(--success-green)' }}
                                                            >
                                                                <SiTicktick />
                                                            </button>
                                                        )}

                                                        {/* Botón Solicitar Cambio (Solo Empleados) */}
                                                        {user?.rol === 'EMPLEADO' && quotation.estado === 'BORRADOR' && (
                                                            <button
                                                                className="btn-icon action-btn-custom"
                                                                onClick={(e) => { e.stopPropagation(); navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=request`); }}
                                                                title="Solicitar Cambio al Administrador"
                                                                style={{ color: 'var(--primary-orange)' }}
                                                            >
                                                                <FiFlag size={18} style={{ pointerEvents: 'none' }} />
                                                            </button>
                                                        )}



                                                        {/* Botón Ver (Ojo) - Para estados finalizados (Aceptada/Rechazada/Enviada) */}
                                                        {(quotation.estado === 'ACEPTADA' || quotation.estado === 'RECHAZADA' || quotation.estado === 'ENVIADA') && (
                                                            <>
                                                                <button
                                                                    className="btn-icon action-btn-custom"
                                                                    onClick={(e) => { e.stopPropagation(); handleResend(quotation.id); }}
                                                                    title="Re-enviar Notificación"
                                                                    style={{ color: 'var(--blue-600)' }}
                                                                >
                                                                    <FiSend size={18} style={{ pointerEvents: 'none' }} />
                                                                </button>

                                                                {/* Botón Crear Oferta (Solo si está ENVIADA y NO es ya una oferta) */}
                                                                {quotation.estado === 'ENVIADA' && !quotation.es_oferta && (
                                                                    <button
                                                                        className="btn-icon action-btn-custom"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedQuotationForOffer(quotation);
                                                                            setShowOfferModal(true);
                                                                        }}
                                                                        title="Crear Oferta Especial"
                                                                        style={{ color: 'var(--primary-orange)' }}
                                                                    >
                                                                        <FiTag size={18} style={{ pointerEvents: 'none' }} />
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Botón Eliminar (Solo Admin) */}
                                                        {user?.rol === 'ADMIN' && (
                                                            <button
                                                                className="btn-icon action-btn-custom"
                                                                onClick={(e) => { e.stopPropagation(); handleDelete(quotation.id); }}
                                                                title="Eliminar"
                                                                style={{ color: 'var(--danger-red)' }}
                                                            >
                                                                <FiTrash2 size={18} style={{ pointerEvents: 'none' }} />
                                                            </button>
                                                        )}

                                                        <button
                                                            className="btn-icon action-btn-custom"
                                                            onClick={(e) => { e.stopPropagation(); handleDownloadPdf(quotation.id, quotation.numero); }}
                                                            title="Descargar PDF"
                                                            style={{ color: 'var(--blue-600)' }}
                                                        >
                                                            <FiDownload size={18} style={{ pointerEvents: 'none' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Vista Móvil: Tarjetas Apiladas */}
                        <div className="d-block d-lg-none mt-3">
                            {quotations.map((quotation) => {
                                const statusInfo = quotationsService.getStatusInfo(quotation.estado);
                                return (
                                    <div
                                        key={quotation.id}
                                        className="mobile-card"
                                        onClick={() => {
                                            if (quotation.estado === 'BORRADOR') {
                                                if (user?.rol === 'ADMIN') {
                                                    navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}`);
                                                } else { // EMPLEADO
                                                    navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=approval`);
                                                }
                                            } else { // ENVIADA, ACEPTADA, RECHAZADA
                                                navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=view`);
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="mobile-card-header">
                                            <span className="mobile-card-id">{quotation.numero}</span>
                                            <span className={`badge-custom ${statusInfo.class}`}>
                                                {statusInfo.label}
                                            </span>
                                        </div>
                                         <div className="mobile-card-body">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div className="mobile-card-title">{quotation.cliente_info?.nombre || 'N/A'}</div>
                                                {quotation.es_oferta && (
                                                    <span style={{
                                                        fontSize: '10px',
                                                        color: '#059669',
                                                        background: '#d1fae5',
                                                        padding: '1px 6px',
                                                        borderRadius: '4px',
                                                        fontWeight: '700'
                                                    }}>OFERTA</span>
                                                )}
                                            </div>
                                            <div className="mobile-card-subtitle">{new Date(quotation.fecha_creacion).toLocaleDateString('es-CL')}</div>
                                        </div>
                                        <div className="mobile-card-footer">
                                            <div className="mobile-card-total">{formatCurrency(quotation.total)}</div>
                                            <div className="mobile-card-actions">
                                                {/* Botón Revisar/Aprobar */}
                                                {quotation.estado === 'BORRADOR' && (
                                                    <button className="btn-icon action-btn-custom" onClick={(e) => { e.stopPropagation(); navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=approval`); }} style={{ color: 'var(--success-green)' }}>
                                                        <FiEye size={20} />
                                                    </button>
                                                )}
                                                {/* Botón Solicitar Cambio */}
                                                {user?.rol === 'EMPLEADO' && quotation.estado === 'BORRADOR' && (
                                                    <button className="btn-icon action-btn-custom" onClick={(e) => { e.stopPropagation(); navigate(`/app/cotizaplus/cotizaciones/editar/${quotation.id}?mode=request`); }} style={{ color: 'var(--primary-orange)' }}>
                                                        <FiFlag size={18} />
                                                    </button>
                                                )}

                                                {/* Botón Ver/Reenviar */}
                                                {(quotation.estado === 'ACEPTADA' || quotation.estado === 'RECHAZADA' || quotation.estado === 'ENVIADA') && (
                                                    <>
                                                        <button className="btn-icon action-btn-custom" onClick={(e) => { e.stopPropagation(); handleResend(quotation.id); }} style={{ color: 'var(--blue-600)' }}>
                                                            <FiSend size={18} />
                                                        </button>
                                                    </>
                                                )}
                                                {/* Botón Eliminar */}
                                                {user?.rol === 'ADMIN' && (
                                                    <button className="btn-icon action-btn-custom" onClick={(e) => { e.stopPropagation(); handleDelete(quotation.id); }} style={{ color: 'var(--danger-red)' }}>
                                                        <FiTrash2 size={18} />
                                                    </button>
                                                )}
                                                {/* Botón Descargar */}
                                                <button className="btn-icon action-btn-custom" onClick={(e) => { e.stopPropagation(); handleDownloadPdf(quotation.id, quotation.numero); }} style={{ color: 'var(--blue-600)' }}>
                                                    <FiDownload size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                )}
            </div>

            {/* Offer Modal */}
            {showOfferModal && selectedQuotationForOffer && (
                <OfferModal
                    show={showOfferModal}
                    handleClose={() => {
                        setShowOfferModal(false);
                        setSelectedQuotationForOffer(null);
                    }}
                    quotation={selectedQuotationForOffer}
                    onOfferCreated={() => {
                        setShowOfferModal(false);
                        setSelectedQuotationForOffer(null);
                        fetchQuotations(currentPage, searchTerm, statusFilter);
                    }}
                />
            )}


            {/* Botón Flotante para Móviles */}
            <button
                className="fab-custom d-lg-none"
                onClick={() => navigate('/app/cotizaplus/cotizaciones/nuevo')}
                title="Nueva Cotización"
            >
                <FiPlus size={28} />
            </button>
        </div >
    );
};

export default QuotationList;
